import { useState, useMemo } from 'react';
import { Card, CardBody, Button, Input, Badge } from '@img/ui';
import { SEO } from '../components/SEO';
import { KEYWORDS_CALCULATORS } from '../lib/seo-keywords';

type CalcId = 'mortgage' | 'zakat' | 'commission' | 'roi' | 'construction' | 'rent_yield' | 'stamp_duty' | 'down_payment';

export function CalculatorsPage() {
  const [active, setActive] = useState<CalcId>('mortgage');
  const calcs: Array<{ id: CalcId; label: string; icon: string; desc: string }> = [
    { id: 'mortgage',     label: 'القرض العقاري',          icon: '🏦', desc: 'حساب القسط الشهري والفائدة الإجمالية' },
    { id: 'zakat',        label: 'زكاة العقار',            icon: '💰', desc: 'احسب زكاة عقاراتك السنوية' },
    { id: 'commission',   label: 'سعي الوسيط',             icon: '🤝', desc: 'حساب 2.5% أو نسبة مخصصة' },
    { id: 'roi',          label: 'العائد الاستثماري',      icon: '📈', desc: 'ROI من الإيجار السنوي' },
    { id: 'construction', label: 'تكلفة البناء',           icon: '🏗️', desc: 'تقدير تكلفة بناء فيلا أو عمارة' },
    { id: 'rent_yield',   label: 'عائد الإيجار',           icon: '🔑', desc: 'الإيجار السنوي مقابل قيمة العقار' },
    { id: 'stamp_duty',   label: 'رسوم نقل الملكية',       icon: '📋', desc: 'رسوم البلدية والـ VAT' },
    { id: 'down_payment', label: 'الدفعة المقدمة',         icon: '💵', desc: 'احسب الحد الأدنى للدفعة المقدمة' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title="الحاسبات العقارية الذكية 🧮 ٨ حاسبات مجانية — العقار بصورة جديدة"
        description="العقار بصورة جديدة — حاسبات عقارية مجانية احترافية للسوق السعودي: حاسبة الرهن العقاري والقسط الشهري، حاسبة زكاة العقار، حاسبة عمولة الوسيط (سعي 2.5%)، حاسبة العائد الاستثماري ROI، حاسبة تكلفة البناء، حاسبة عائد الإيجار، حاسبة رسوم نقل الملكية، حاسبة الدفعة المقدمة، حاسبة ضريبة التصرفات العقارية. شركة صورة العقارية — العقار بصورة جديدة."
        keywords={KEYWORDS_CALCULATORS}
        canonical="https://img.sa/calculators"
        jsonLd={{
          '@type': 'WebApplication',
          name: 'الحاسبات العقارية الذكية — العقار بصورة جديدة',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'SAR' },
          url: 'https://img.sa/calculators',
          publisher: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            slogan: 'العقار بصورة جديدة',
            url: 'https://img.sa',
          },
          description: 'العقار بصورة جديدة — 8+ حاسبات عقارية ذكية مجانية لكل التعاملات في السوق السعودي',
          featureList: [
            'حاسبة الرهن العقاري والقسط الشهري',
            'حاسبة زكاة العقار السنوية',
            'حاسبة عمولة الوسيط (السعي) 2.5%',
            'حاسبة العائد الاستثماري ROI',
            'حاسبة تكلفة البناء للفلل والعمائر',
            'حاسبة عائد الإيجار السنوي',
            'حاسبة رسوم نقل الملكية والـ VAT',
            'حاسبة الدفعة المقدمة للقرض العقاري',
            'حاسبة ضريبة التصرفات العقارية 5%',
            'حاسبة المساحة وتحويل الوحدات',
          ],
        }}
      />
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-black text-navy-dark mb-2">🧮 الحاسبات العقارية</h1>
        <p className="text-ink-secondary">8 حاسبات احترافية تساعدك على اتخاذ قرارك العقاري بثقة</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* قائمة الحاسبات */}
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-brand p-3 sticky top-24">
            {calcs.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                className={`w-full text-right px-3 py-3 rounded-xl mb-1 transition flex items-center gap-3 ${
                  active === c.id ? 'bg-navy-dark text-white shadow-brand' : 'hover:bg-gray-50 text-ink'
                }`}
              >
                <span className="text-xl">{c.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{c.label}</div>
                  <div className={`text-[10px] ${active === c.id ? 'text-white/70' : 'text-ink-muted'}`}>{c.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* المحتوى */}
        <main className="lg:col-span-3">
          {active === 'mortgage'     && <MortgageCalc />}
          {active === 'zakat'        && <ZakatCalc />}
          {active === 'commission'   && <CommissionCalc />}
          {active === 'roi'          && <RoiCalc />}
          {active === 'construction' && <ConstructionCalc />}
          {active === 'rent_yield'   && <RentYieldCalc />}
          {active === 'stamp_duty'   && <StampDutyCalc />}
          {active === 'down_payment' && <DownPaymentCalc />}
        </main>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 1. قرض عقاري ━━━━━━━━━━━━━━━━━━━━
function MortgageCalc() {
  const [principal, setPrincipal] = useState('500000');
  const [rate, setRate] = useState('5.5');
  const [years, setYears] = useState('20');

  const result = useMemo(() => {
    const P = Number(principal) || 0;
    const r = (Number(rate) || 0) / 100 / 12;
    const n = (Number(years) || 0) * 12;
    if (!P || !r || !n) return null;
    const monthly = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = monthly * n;
    const interest = total - P;
    return { monthly, total, interest };
  }, [principal, rate, years]);

  return (
    <Wrap title="🏦 حاسبة القرض العقاري" subtitle="احسب قسطك الشهري وإجمالي الفوائد على القرض">
      <Grid>
        <NumInput label="مبلغ القرض (ر.س)" value={principal} onChange={setPrincipal} prefix="ر.س" />
        <NumInput label="نسبة الفائدة السنوية (%)" value={rate} onChange={setRate} suffix="%" step="0.1" />
        <NumInput label="مدة القرض (سنوات)" value={years} onChange={setYears} suffix="سنة" />
      </Grid>

      {result && (
        <Results>
          <ResultBig label="القسط الشهري" value={result.monthly} unit="ر.س/شهر" highlight />
          <ResultRow label="إجمالي المدفوعات" value={result.total} unit="ر.س" />
          <ResultRow label="إجمالي الفوائد" value={result.interest} unit="ر.س" color="warn" />
          <Insight>
            💡 على مدى {years} سنة، ستدفع <b>{(result.interest / Number(principal) * 100).toFixed(1)}%</b> فوائد إضافية على القرض الأصلي.
          </Insight>
        </Results>
      )}
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 2. زكاة العقار ━━━━━━━━━━━━━━━━━━━━
//
// 🧠 الفقه الزكوي للعقار:
//   • للسكن الشخصي: لا زكاة
//   • للتجارة (نية البيع): 2.5% من القيمة السوقية سنوياً (يحسب الحول)
//   • للإيجار: لا زكاة على ذات العقار، الزكاة على المُدّخر من الأجرة الذي حال عليه الحول
//     ⇒ نحسب: (رصيد الأجرة المتبقي بعد المصروفات والذي مرّ عليه سنة) × 2.5%
//
function ZakatCalc() {
  const [purpose, setPurpose] = useState<'rent' | 'trade' | 'personal'>('trade');
  const [value, setValue] = useState('1000000');
  const [savedRent, setSavedRent] = useState('20000'); // المُدّخر من الأجرة الذي حال عليه الحول
  const [debts, setDebts] = useState('0');

  const result = useMemo(() => {
    const v = Number(value) || 0;
    const s = Number(savedRent) || 0;
    const d = Number(debts) || 0;
    if (purpose === 'personal') return { zakat: 0, base: 0 };
    if (purpose === 'rent') {
      // الزكاة على المُدّخر من الأجرة (وليس على الأجرة كلها أو على ذات العقار)
      const base = Math.max(0, s - d);
      return { zakat: base * 0.025, base };
    }
    // عقار للتجارة: زكاة 2.5% من قيمته السوقية بعد طرح الديون عليه
    const base = Math.max(0, v - d);
    return { zakat: base * 0.025, base };
  }, [purpose, value, savedRent, debts]);

  return (
    <Wrap title="💰 حاسبة زكاة العقار" subtitle="حدد نوع العقار لاحتساب الزكاة الشرعية">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        {[
          { id: 'trade', label: '🏪 للتجارة (للبيع)', desc: 'تجب الزكاة (2.5% من القيمة)' },
          { id: 'rent', label: '🏠 للإيجار', desc: 'زكاة 2.5% من صافي الدخل' },
          { id: 'personal', label: '🛏️ للسكن الشخصي', desc: 'لا تجب الزكاة' },
        ].map((p) => (
          <button key={p.id} onClick={() => setPurpose(p.id as never)}
            className={`p-3 rounded-xl border-2 text-right transition ${purpose === p.id ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="font-bold">{p.label}</div>
            <div className="text-xs text-ink-muted">{p.desc}</div>
          </button>
        ))}
      </div>

      {purpose !== 'personal' && (
        <>
          <Grid>
            {purpose === 'trade' && <NumInput label="القيمة السوقية (ر.س)" value={value} onChange={setValue} prefix="ر.س" hint="السعر التقديري لو بِيع اليوم" />}
            {purpose === 'rent'  && (
              <NumInput
                label="المُدّخر من الأجرة الذي حال عليه الحول (ر.س)"
                value={savedRent}
                onChange={setSavedRent}
                prefix="ر.س"
                hint="المبلغ المتبقي من الإيجار بعد المصروفات اللي مرّت عليه سنة"
              />
            )}
            <NumInput label="الديون والمصروفات المخصومة (ر.س)" value={debts} onChange={setDebts} prefix="ر.س" />
          </Grid>
          <Results>
            <ResultBig label="مبلغ الزكاة المستحق" value={result.zakat} unit="ر.س" highlight />
            <ResultRow label="القاعدة الخاضعة للزكاة" value={result.base} unit="ر.س" />
            {purpose === 'rent' && (
              <Insight>
                📜 <b>تنبيه فقهي:</b> العقار المُؤجَّر لا زكاة على عينه — الزكاة على ما يُدّخر من الأجرة ويبلغ النصاب ويحول عليه الحول.
              </Insight>
            )}
            {purpose === 'trade' && (
              <Insight>
                📜 <b>ملاحظة:</b> العقار المعدّ للتجارة (بنية البيع) تجب فيه الزكاة سنوياً 2.5% من قيمته السوقية وقت الحول.
              </Insight>
            )}
            <Insight>
              ℹ️ هذه حاسبة تقريبية. للحالات الخاصة (شراكة، أرض غير مسجّلة، عقار مؤجّر بعقد طويل...) راجع جهة شرعية معتمدة.
            </Insight>
          </Results>
        </>
      )}

      {purpose === 'personal' && (
        <Results>
          <Insight>
            ✅ <b>لا زكاة على العقار المخصص للسكن الشخصي</b> (السكن الأساسي للأسرة). الزكاة تجب فقط على عقارات التجارة والاستثمار.
          </Insight>
        </Results>
      )}
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 3. سعي الوسيط ━━━━━━━━━━━━━━━━━━━━
function CommissionCalc() {
  const [price, setPrice] = useState('1000000');
  const [rate, setRate] = useState('2.5');
  const [vatIncluded, setVatIncluded] = useState(true);

  const result = useMemo(() => {
    const p = Number(price) || 0;
    const r = (Number(rate) || 0) / 100;
    const commission = p * r;
    const vat = commission * 0.15;
    return { commission, vat, total: vatIncluded ? commission + vat : commission };
  }, [price, rate, vatIncluded]);

  return (
    <Wrap title="🤝 حاسبة سعي الوسيط" subtitle="السعي القياسي 2.5% — قابل للتعديل">
      <Grid>
        <NumInput label="قيمة العقار (ر.س)" value={price} onChange={setPrice} prefix="ر.س" />
        <NumInput label="نسبة السعي (%)" value={rate} onChange={setRate} suffix="%" step="0.1" />
      </Grid>
      <label className="flex items-center gap-2 mb-4">
        <input type="checkbox" checked={vatIncluded} onChange={(e) => setVatIncluded(e.target.checked)} className="w-5 h-5 accent-navy" />
        <span className="text-sm font-bold">إضافة ضريبة القيمة المضافة (15%)</span>
      </label>

      <Results>
        <ResultBig label="السعي" value={result.commission} unit="ر.س" highlight />
        {vatIncluded && <ResultRow label="ضريبة القيمة المضافة (15%)" value={result.vat} unit="ر.س" />}
        <ResultRow label="الإجمالي المستحق" value={result.total} unit="ر.س" color="success" />
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 4. العائد الاستثماري ━━━━━━━━━━━━━━━━━━━━
function RoiCalc() {
  const [purchase, setPurchase] = useState('1500000');
  const [annualRent, setAnnualRent] = useState('90000');
  const [annualCosts, setAnnualCosts] = useState('5000');
  const [appreciation, setAppreciation] = useState('3');

  const result = useMemo(() => {
    const P = Number(purchase) || 1;
    const R = Number(annualRent) || 0;
    const C = Number(annualCosts) || 0;
    const A = (Number(appreciation) || 0) / 100;
    const grossYield = (R / P) * 100;
    const netYield = ((R - C) / P) * 100;
    const totalReturn = netYield + (A * 100);
    const paybackYears = R - C > 0 ? P / (R - C) : 0;
    return { grossYield, netYield, totalReturn, paybackYears, netCashflow: R - C };
  }, [purchase, annualRent, annualCosts, appreciation]);

  return (
    <Wrap title="📈 حاسبة العائد الاستثماري (ROI)" subtitle="هل العقار استثمار مربح؟">
      <Grid>
        <NumInput label="قيمة الشراء (ر.س)" value={purchase} onChange={setPurchase} prefix="ر.س" />
        <NumInput label="الإيجار السنوي (ر.س)" value={annualRent} onChange={setAnnualRent} prefix="ر.س" />
        <NumInput label="المصاريف السنوية (صيانة/تأمين/فراغ)" value={annualCosts} onChange={setAnnualCosts} prefix="ر.س" />
        <NumInput label="معدّل ارتفاع القيمة (%/سنة)" value={appreciation} onChange={setAppreciation} suffix="%" step="0.5" />
      </Grid>

      <Results>
        <ResultBig label="العائد الإجمالي السنوي" value={result.totalReturn} unit="%" highlight />
        <ResultRow label="عائد الإيجار الإجمالي" value={result.grossYield} unit="%" />
        <ResultRow label="عائد الإيجار الصافي" value={result.netYield} unit="%" />
        <ResultRow label="التدفق النقدي السنوي الصافي" value={result.netCashflow} unit="ر.س" />
        <ResultRow label="فترة الاسترداد" value={result.paybackYears} unit="سنة" />
        <Insight>
          {result.totalReturn >= 8 ? '🟢 استثمار قوي' : result.totalReturn >= 5 ? '🟡 استثمار متوسط' : '🔴 عائد منخفض'}
          {' — '}
          المعدل المتوسط للسوق العقاري السعودي: 6-9% سنوياً.
        </Insight>
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 5. تكلفة البناء ━━━━━━━━━━━━━━━━━━━━
//
// 🧠 منطق الحاسبة المعتمد على نسب واقعية في السوق السعودي 2026:
//
//   • الدور الأرضي يتحمّل: الأساسات، الحفر، الأعمال الخارجية، السور، اللاندسكيب
//   • الأدوار العلوية (ما عدا الأخير): لا أساسات، لا حفر، لا خارجي ⇒ ~75% من الأرضي
//   • الدور الأخير: مثل العلوي + عزل السقف وخزانات وميازيب ⇒ ~83% من الأرضي
//   • القبو: حفر ضخم + عزل مائي + جدران ساندة ⇒ ~135% من الأرضي
//   • غرفة السطح: مساحة جزئية + تشطيبات أبسط ⇒ ~60% × مساحة جزئية
//
// المرجع: متوسطات سوق المقاولات السعودي + قاعدة "البناء التراكمي"
//
function ConstructionCalc() {
  const [area, setArea] = useState('300');
  const [floors, setFloors] = useState('2');
  const [hasBasement, setHasBasement] = useState(false);
  const [hasRoofRoom, setHasRoofRoom] = useState(false);
  const [roofRoomPct, setRoofRoomPct] = useState('30'); // نسبة مساحة غرفة السطح من الدور
  const [quality, setQuality] = useState<'economic' | 'standard' | 'premium'>('standard');

  const result = useMemo(() => {
    const a = Number(area) || 0;
    const f = Math.max(1, Number(floors) || 1);
    const rrPct = Math.max(10, Math.min(100, Number(roofRoomPct) || 30)) / 100;
    const rates = {
      economic: { perSqm: 1500, label: 'اقتصادي' },
      standard: { perSqm: 2200, label: 'قياسي' },
      premium:  { perSqm: 3500, label: 'فاخر' },
    }[quality];

    // 📏 تركيبة الأدوار مع المعامل النسبي للدور الأرضي
    type FloorRow = { name: string; area: number; multiplier: number; cost: number; note: string };
    const floorsList: FloorRow[] = [];

    if (hasBasement) {
      floorsList.push({
        name: '🚪 القبو', area: a, multiplier: 1.35,
        cost: a * rates.perSqm * 1.35,
        note: 'حفر عميق + عزل مائي + جدران ساندة',
      });
    }

    floorsList.push({
      name: '🏠 الدور الأرضي', area: a, multiplier: 1.00,
      cost: a * rates.perSqm,
      note: 'الأساسات + الحفر + اللاندسكيب + السور',
    });

    for (let i = 1; i < f; i++) {
      const isTop = i === f - 1 && !hasRoofRoom;
      const m = isTop ? 0.83 : 0.75;
      const label = i === 1 ? 'الأول' : i === 2 ? 'الثاني' : i === 3 ? 'الثالث' : i === 4 ? 'الرابع' : `${i}`;
      floorsList.push({
        name: `🏢 الدور ${label}${isTop ? ' (الأخير)' : ''}`, area: a, multiplier: m,
        cost: a * rates.perSqm * m,
        note: isTop ? 'بدون أساسات + يحتاج عزل سقف وخزانات' : 'هيكل + جدران + تشطيبات (بدون أساسات/خارجي)',
      });
    }

    if (hasRoofRoom) {
      const rrArea = a * rrPct;
      floorsList.push({
        name: '🌅 غرفة السطح', area: rrArea, multiplier: 0.60,
        cost: rrArea * rates.perSqm * 0.60,
        note: 'مساحة جزئية + تشطيبات أبسط + عزل سطح',
      });
    }

    const totalArea = floorsList.reduce((s, fl) => s + fl.area, 0);
    const total = floorsList.reduce((s, fl) => s + fl.cost, 0);

    // 📊 التوزيع التفصيلي على البنود — لكل بند نحسبه من المساحة المناسبة
    const groundCost = a * rates.perSqm;            // مرجع للبنود الأرضية فقط
    const basementCost = hasBasement ? a * rates.perSqm * 1.35 : 0;

    const breakdown = {
      // الأساسات + الحفر: الأرضي + القبو فقط
      foundation:   groundCost * 0.10 + basementCost * 0.20,
      excavation:   groundCost * 0.03 + basementCost * 0.12,
      // الهيكل والجدران والتشطيبات والكهرباء: على كل المساحة المبنية بنسبتها
      structure:    total * 0.18,
      walls:        total * 0.12,
      slabs:        total * 0.07,
      finishing:    total * 0.27,
      mep:          total * 0.11,
      // عزل السقف الأخير: على آخر دور فقط
      roofing:      a * rates.perSqm * 0.06,
      // الأعمال الخارجية: على الأرضي فقط
      exterior:     groundCost * 0.06,
    };

    return {
      totalArea, total,
      avgPerSqm: totalArea > 0 ? total / totalArea : 0,
      baseRate: rates.perSqm,
      label: rates.label,
      floorsList,
      breakdown,
    };
  }, [area, floors, hasBasement, hasRoofRoom, roofRoomPct, quality]);

  return (
    <Wrap title="🏗️ حاسبة تكلفة البناء" subtitle="تقدير تكلفة البناء بنسب واقعية لكل دور — لأن الأدوار ما تكلفتها وحدة">
      <Grid>
        <NumInput label="مساحة الدور (م²)" value={area} onChange={setArea} suffix="م²" hint="الدور الواحد، مثلاً 300 م²" />
        <NumInput label="عدد الأدوار فوق الأرض" value={floors} onChange={setFloors} hint="بدون قبو وبدون غرفة السطح" />
      </Grid>

      {/* خيارات إضافية */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        <label className={`p-3 rounded-xl border-2 cursor-pointer flex items-center gap-2 transition ${hasBasement ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
          <input type="checkbox" checked={hasBasement} onChange={(e) => setHasBasement(e.target.checked)} className="w-5 h-5 accent-navy" />
          <div>
            <div className="font-bold text-sm">🚪 قبو</div>
            <div className="text-[10px] text-ink-muted">+35% أغلى من الأرضي</div>
          </div>
        </label>
        <label className={`p-3 rounded-xl border-2 cursor-pointer flex items-center gap-2 transition ${hasRoofRoom ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
          <input type="checkbox" checked={hasRoofRoom} onChange={(e) => setHasRoofRoom(e.target.checked)} className="w-5 h-5 accent-navy" />
          <div>
            <div className="font-bold text-sm">🌅 غرفة سطح</div>
            <div className="text-[10px] text-ink-muted">جزء من السطح + خدمات</div>
          </div>
        </label>
        {hasRoofRoom && (
          <NumInput label="نسبة مساحة غرفة السطح (%)" value={roofRoomPct} onChange={setRoofRoomPct} suffix="%" hint="من مساحة الدور — عادة 25-40%" />
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {(['economic', 'standard', 'premium'] as const).map((q) => (
          <button key={q} onClick={() => setQuality(q)}
            className={`p-3 rounded-xl border-2 text-center transition ${quality === q ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="font-bold text-sm">{q === 'economic' ? '💵 اقتصادي' : q === 'standard' ? '⭐ قياسي' : '💎 فاخر'}</div>
            <div className="text-[10px] text-ink-muted">{q === 'economic' ? '1,500 ر.س/م²' : q === 'standard' ? '2,200 ر.س/م²' : '3,500 ر.س/م²'}</div>
          </button>
        ))}
      </div>

      <Results>
        <ResultBig label="إجمالي التكلفة التقديرية" value={result.total} unit="ر.س" highlight />
        <ResultRow label="إجمالي المساحة المبنية" value={result.totalArea} unit="م²" />
        <ResultRow label="السعر المرجعي للجودة" value={result.baseRate} unit="ر.س/م² (الأرضي)" />
        <ResultRow label="متوسط التكلفة الفعلي" value={result.avgPerSqm} unit="ر.س/م²" />

        {/* 📐 تفصيل الأدوار — هذا الجزء الأهم اللي طلبه المستخدم */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-3">
          <div className="font-bold text-sm mb-2 text-blue-900">📐 تفصيل التكلفة لكل دور (نسب واقعية):</div>
          <div className="space-y-2">
            {result.floorsList.map((fl, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-blue-100">
                <div className="flex justify-between items-start gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-navy-dark">{fl.name}</div>
                    <div className="text-[11px] text-ink-muted">{fl.note}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-black text-navy-dark">{fl.cost.toLocaleString('en-US', { maximumFractionDigits: 0 })} ر.س</div>
                    <div className="text-[10px] text-ink-muted">
                      {fl.area.toFixed(0)} م² × {(fl.multiplier * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-3 mt-2">
          <div className="font-bold text-sm mb-2">📊 توزيع التكلفة حسب البند:</div>
          <ResultRow label="الأساسات" value={result.breakdown.foundation} unit="ر.س" small />
          <ResultRow label="الحفر والردم" value={result.breakdown.excavation} unit="ر.س" small />
          <ResultRow label="الهيكل (خرسانة + حديد)" value={result.breakdown.structure} unit="ر.س" small />
          <ResultRow label="الجدران والقواطع" value={result.breakdown.walls} unit="ر.س" small />
          <ResultRow label="الأسقف البينية" value={result.breakdown.slabs} unit="ر.س" small />
          <ResultRow label="عزل السقف الأخير" value={result.breakdown.roofing} unit="ر.س" small />
          <ResultRow label="التشطيبات (سيراميك/طلاء/أبواب)" value={result.breakdown.finishing} unit="ر.س" small />
          <ResultRow label="الكهرباء والسباكة والتكييف" value={result.breakdown.mep} unit="ر.س" small />
          <ResultRow label="الأعمال الخارجية (سور/لاندسكيب)" value={result.breakdown.exterior} unit="ر.س" small />
        </div>
        <Insight>
          💡 <b>لماذا الأدوار مختلفة؟</b> الدور الأرضي يحمل تكاليف الأساسات والحفر والأعمال الخارجية،
          بينما الأدوار العلوية تستفيد من البنية التحتية القائمة. القبو أغلى لأنه يحتاج حفر عميق وعزل مائي.
        </Insight>
        <Insight>⚠️ تقديرات سوق 2026. الأسعار الفعلية تتفاوت حسب المدينة والمقاول وجودة المواد بنسبة ±15%.</Insight>
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 6. عائد الإيجار ━━━━━━━━━━━━━━━━━━━━
function RentYieldCalc() {
  const [propertyValue, setPropertyValue] = useState('800000');
  const [monthlyRent, setMonthlyRent] = useState('3500');

  const result = useMemo(() => {
    const v = Number(propertyValue) || 1;
    const m = Number(monthlyRent) || 0;
    const yearly = m * 12;
    const yieldPct = (yearly / v) * 100;
    return { yearly, yieldPct };
  }, [propertyValue, monthlyRent]);

  return (
    <Wrap title="🔑 حاسبة عائد الإيجار" subtitle="نسبة العائد السنوي من الإيجار">
      <Grid>
        <NumInput label="قيمة العقار (ر.س)" value={propertyValue} onChange={setPropertyValue} prefix="ر.س" />
        <NumInput label="الإيجار الشهري (ر.س)" value={monthlyRent} onChange={setMonthlyRent} prefix="ر.س" />
      </Grid>

      <Results>
        <ResultBig label="نسبة العائد السنوي" value={result.yieldPct} unit="%" highlight />
        <ResultRow label="الإيجار السنوي" value={result.yearly} unit="ر.س" />
        <Insight>
          {result.yieldPct >= 7 ? '🟢 عائد ممتاز' : result.yieldPct >= 5 ? '🟡 عائد جيد' : '🔴 عائد منخفض'}
        </Insight>
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 7. رسوم نقل الملكية ━━━━━━━━━━━━━━━━━━━━
//
// 🧠 المرجع الرسمي (لائحة ضريبة التصرفات العقارية – السعودية):
//   • ضريبة التصرفات العقارية (RETT) 5% — يدفعها المشتري فعلياً
//     (البائع هو "المُكلَّف" بالتحصيل والتوريد لـ ZATCA — لا يدفعها من جيبه)
//   • إعفاء أول مسكن للسعوديين حتى 1,000,000 ر.س — للمشتري فقط
//     (شرط: سعودي + أول عقار سكني + للسكن الشخصي)
//   • سعي الوسيط: 2.5% (متفق عليه عرفاً) + VAT 15% — قابل للتفاوض على من يدفعه
//   • رسوم التوثيق والنقل: ~1,000 ر.س (وزارة العدل)
//
function StampDutyCalc() {
  const [price, setPrice] = useState('1000000');
  const [isFirstHome, setIsFirstHome] = useState(false);
  const [agentFeeRate, setAgentFeeRate] = useState('2.5');
  const [agentFeePaidBy, setAgentFeePaidBy] = useState<'buyer' | 'seller' | 'split'>('buyer');

  const result = useMemo(() => {
    const p = Number(price) || 0;
    const aRate = (Number(agentFeeRate) || 0) / 100;

    // 🏛️ ضريبة التصرفات العقارية 5% — يدفعها المشتري فعلياً
    // البائع يحصّلها ويوردها لـ ZATCA (مجرد محصِّل)
    const exemption = isFirstHome ? Math.min(p, 1_000_000) : 0;
    const taxableAmount = Math.max(0, p - exemption);
    const rett = taxableAmount * 0.05;

    // 🤝 سعي + VAT
    const agentFeeBase = p * aRate;
    const agentVat = agentFeeBase * 0.15;
    const agentTotal = agentFeeBase + agentVat;

    // 📜 رسوم التوثيق (موحّد على المشتري عادة)
    const notaryFee = 1000;
    const registrationFee = Math.min(p * 0.001, 5000);
    const adminFees = notaryFee + registrationFee;

    // 💰 توزيع السعي حسب الاتفاق
    const buyerAgent  = agentFeePaidBy === 'buyer'  ? agentTotal : agentFeePaidBy === 'split' ? agentTotal / 2 : 0;
    const sellerAgent = agentFeePaidBy === 'seller' ? agentTotal : agentFeePaidBy === 'split' ? agentTotal / 2 : 0;

    // 🛒 المشتري يدفع: RETT (إن لم يكن مُعفى) + التوثيق + جزؤه من السعي
    const buyerTotal  = rett + adminFees + buyerAgent;

    // 💼 البائع يدفع: جزؤه من السعي فقط (الـ RETT يحصّلها من المشتري ويوردها)
    const sellerTotal = sellerAgent;

    return {
      rett, exemption, taxableAmount,
      agentFeeBase, agentVat, agentTotal,
      notaryFee, registrationFee, adminFees,
      buyerAgent, sellerAgent,
      buyerTotal, sellerTotal,
      grandTotal: buyerTotal + sellerTotal,
    };
  }, [price, isFirstHome, agentFeeRate, agentFeePaidBy]);

  return (
    <Wrap title="📋 رسوم نقل الملكية والتصرفات العقارية" subtitle="ضريبة التصرفات العقارية (RETT) 5% — حسب الأنظمة السعودية الحالية">
      <Grid>
        <NumInput label="قيمة العقار (ر.س)" value={price} onChange={setPrice} prefix="ر.س" />
        <NumInput label="نسبة سعي الوسيط (%)" value={agentFeeRate} onChange={setAgentFeeRate} suffix="%" step="0.1" hint="القياسي 2.5%" />
      </Grid>

      {/* 🏡 إعفاء المشتري — أول مسكن */}
      <label className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-success-bg border border-success/30 cursor-pointer">
        <input type="checkbox" checked={isFirstHome} onChange={(e) => setIsFirstHome(e.target.checked)} className="w-5 h-5 accent-navy mt-0.5" />
        <div>
          <span className="text-sm font-bold">🏡 المشتري سعودي ويشتري أول مسكن (إعفاء RETT)</span>
          <p className="text-[11px] text-ink-muted mt-1">
            الإعفاء يُطبَّق على <b>المشتري</b> فقط — يعفى من 5% RETT حتى 1,000,000 ر.س.
            الشروط: سعودي + أول عقار سكني + للسكن الشخصي.
          </p>
        </div>
      </label>

      {/* 🤝 من يدفع السعي؟ */}
      <div className="mb-4">
        <label className="block text-sm font-bold text-ink mb-2">من يدفع سعي الوسيط؟</label>
        <div className="grid grid-cols-3 gap-2">
          {([
            { id: 'buyer',  label: '🛒 المشتري',   desc: 'الأشهر' },
            { id: 'seller', label: '💼 البائع',    desc: 'بالاتفاق' },
            { id: 'split',  label: '⚖️ مناصفة',    desc: '50/50' },
          ] as const).map((o) => (
            <button key={o.id} onClick={() => setAgentFeePaidBy(o.id)}
              className={`p-2.5 rounded-xl border-2 text-center transition ${agentFeePaidBy === o.id ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="font-bold text-sm">{o.label}</div>
              <div className="text-[10px] text-ink-muted">{o.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <Results>
        {/* 🛒 إجمالي ما يدفعه المشتري */}
        <ResultBig label="إجمالي ما يدفعه المشتري" value={result.buyerTotal} unit="ر.س" highlight />

        <div className="bg-gray-50 rounded-xl p-3 mt-2">
          <div className="font-bold text-sm mb-2">🛒 تفصيل المشتري:</div>
          <ResultRow
            label={`ضريبة التصرفات العقارية (5%)${isFirstHome ? ' — بعد الإعفاء' : ''}`}
            value={result.rett} unit="ر.س" small color={isFirstHome && result.exemption > 0 ? 'success' : 'warn'}
          />
          {isFirstHome && result.exemption > 0 && (
            <ResultRow label="✅ المبلغ المُعفى من RETT" value={result.exemption} unit="ر.س" small color="success" />
          )}
          <ResultRow label="رسوم التوثيق (وزارة العدل)" value={result.notaryFee} unit="ر.س" small />
          <ResultRow label="رسم التسجيل العيني" value={result.registrationFee} unit="ر.س" small />
          {result.buyerAgent > 0 && (
            <ResultRow
              label={`السعي${agentFeePaidBy === 'split' ? ' (نصفه)' : ''}`}
              value={result.buyerAgent} unit="ر.س" small
            />
          )}
        </div>

        {/* 💼 على البائع — مجرد محصِّل */}
        <div className={`rounded-xl p-3 mt-2 border ${result.sellerTotal > 0 ? 'bg-amber-50 border-amber-200' : 'bg-success-bg border-success/30'}`}>
          <div className="font-bold text-sm mb-1">💼 على البائع:</div>
          {result.sellerTotal > 0 ? (
            <>
              <ResultRow
                label={`السعي${agentFeePaidBy === 'split' ? ' (نصفه)' : ''}`}
                value={result.sellerAgent} unit="ر.س" small
              />
              <div className="text-[11px] text-ink-muted mt-1">+ <b>تحصيل وتوريد</b> RETT للهيئة (لا يدفعها من جيبه)</div>
            </>
          ) : (
            <div className="text-xs text-success-dark">
              ✅ <b>لا يدفع البائع شيئاً من جيبه</b> — مهمته فقط <b>تحصيل ضريبة التصرفات العقارية من المشتري وتوريدها لهيئة الزكاة والضريبة (ZATCA)</b>.
            </div>
          )}
        </div>

        {/* ملخص شامل */}
        <div className="bg-info-bg border border-info/30 rounded-xl p-3 mt-3 text-xs text-info-dark">
          <div><b>📊 إجمالي الصفقة (مشتري + بائع):</b> {result.grandTotal.toLocaleString('en-US')} ر.س</div>
          <div className="mt-1">≈ <b>{((result.grandTotal / Number(price)) * 100).toFixed(1)}%</b> من قيمة العقار</div>
        </div>

        <Insight>
          📜 <b>ملاحظة قانونية:</b> RETT 5% هي على <b>التصرف العقاري نفسه</b> — البائع هو "المُكلَّف بالتحصيل" قانونياً
          (يحصّلها من المشتري ويوردها لـ ZATCA خلال 30 يوماً)، لكن العبء المالي يقع على المشتري.
          الإعفاء يُمنح للمشتري السعودي على أول مسكن حتى 1,000,000 ر.س.
        </Insight>
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 8. الدفعة المقدمة ━━━━━━━━━━━━━━━━━━━━
//
// 🧠 المرجع: تعليمات البنك المركزي السعودي (ساما) على نسب التمويل العقاري
//   • السعودي + أول مسكن: 5% حد أدنى (يدعم صندوق التنمية العقاري)
//   • السعودي + عقار ثاني: 10-15%
//   • السعودي + عقار استثماري: 30%
//   • مقيم (غير سعودي): 30% حد أدنى
//
function DownPaymentCalc() {
  const [price, setPrice] = useState('800000');
  const [scenario, setScenario] = useState<'saudi_first' | 'saudi_second' | 'saudi_invest' | 'resident'>('saudi_first');
  const [customPct, setCustomPct] = useState<string>('');

  const result = useMemo(() => {
    const p = Number(price) || 0;
    const minPct = {
      saudi_first:  5,
      saudi_second: 15,
      saudi_invest: 30,
      resident:     30,
    }[scenario];
    const pct = customPct ? Math.max(minPct, Number(customPct) || minPct) : minPct;
    const downPayment = p * (pct / 100);
    const loan = p - downPayment;
    // الراتب المطلوب تقريبياً للحصول على هذا القرض (DTI 65% بحد أقصى)
    // افتراض: قرض 25 سنة، فائدة 5.5%
    const monthlyPayment = (() => {
      const r = 0.055 / 12;
      const n = 25 * 12;
      return loan > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : 0;
    })();
    const requiredSalary = monthlyPayment / 0.65;

    return { downPayment, loan, pct, minPct, monthlyPayment, requiredSalary };
  }, [price, scenario, customPct]);

  return (
    <Wrap title="💵 حاسبة الدفعة المقدمة" subtitle="حسب تعليمات ساما — البنك المركزي السعودي">
      <Grid>
        <NumInput label="قيمة العقار (ر.س)" value={price} onChange={setPrice} prefix="ر.س" />
      </Grid>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {[
          { id: 'saudi_first',  label: '🇸🇦 سعودي — أول مسكن',          pct: '5%',  desc: 'مدعوم من صندوق التنمية العقاري' },
          { id: 'saudi_second', label: '🇸🇦 سعودي — عقار ثاني',          pct: '15%', desc: 'يرتفع كلما زاد عدد العقارات' },
          { id: 'saudi_invest', label: '🇸🇦 سعودي — استثماري',           pct: '30%', desc: 'عقار للإيجار أو التجارة' },
          { id: 'resident',     label: '🌐 مقيم (غير سعودي)',            pct: '30%', desc: 'حد أدنى للمقيمين' },
        ].map((s) => (
          <button key={s.id} onClick={() => { setScenario(s.id as never); setCustomPct(''); }}
            className={`p-3 rounded-xl border-2 text-right transition ${scenario === s.id ? 'border-navy bg-navy-soft' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="flex justify-between items-center">
              <div className="font-bold text-sm">{s.label}</div>
              <Badge color="navy">{s.pct}</Badge>
            </div>
            <div className="text-[11px] text-ink-muted mt-1">{s.desc}</div>
          </button>
        ))}
      </div>

      <NumInput
        label={`نسبة دفعة مخصصة (اختياري — الحد الأدنى ${result.minPct}%)`}
        value={customPct}
        onChange={setCustomPct}
        suffix="%"
        hint={`اترك فاضي لاستخدام الحد الأدنى (${result.minPct}%)`}
      />

      <Results>
        <ResultBig label="مبلغ الدفعة المقدمة" value={result.downPayment} unit={`ر.س (${result.pct}%)`} highlight />
        <ResultRow label="مبلغ القرض المتبقي" value={result.loan} unit="ر.س" />
        <ResultRow label="القسط الشهري التقديري" value={result.monthlyPayment} unit="ر.س/شهر (25 سنة @ 5.5%)" />
        <ResultRow label="الراتب الشهري المطلوب تقريباً" value={result.requiredSalary} unit="ر.س/شهر" color="warn" />
        <Insight>
          📌 <b>قاعدة ساما:</b> القسط الشهري يجب ألّا يتجاوز 65% من الدخل الشهري (DTI). الحاسبة تفترض قرضاً لـ25 سنة بفائدة 5.5% للتوضيح.
        </Insight>
      </Results>
    </Wrap>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Components ━━━━━━━━━━━━━━━━━━━━

function Wrap({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <h2 className="text-2xl font-black text-navy-dark mb-1">{title}</h2>
        {subtitle && <p className="text-ink-secondary text-sm mb-6">{subtitle}</p>}
        {children}
      </CardBody>
    </Card>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">{children}</div>;
}

function NumInput({ label, value, onChange, prefix, suffix, hint, step = '1' }: {
  label: string; value: string; onChange: (v: string) => void;
  prefix?: string; suffix?: string; hint?: string; step?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-ink mb-1">{label}</label>
      <div className="relative">
        <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-base font-bold" />
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-ink-muted">{prefix}</span>}
        {suffix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-ink-muted">{suffix}</span>}
      </div>
      {hint && <p className="text-[11px] text-ink-muted mt-1">{hint}</p>}
    </div>
  );
}

function Results({ children }: { children: React.ReactNode }) {
  return <div className="border-t-2 border-dashed border-gray-200 pt-4 space-y-2">{children}</div>;
}

function ResultBig({ label, value, unit, highlight }: { label: string; value: number; unit: string; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl p-5 ${highlight ? 'bg-navy-dark text-white' : 'bg-gray-50'}`}>
      <div className={`text-xs ${highlight ? 'text-white/70' : 'text-ink-muted'} mb-1`}>{label}</div>
      <div className={`text-3xl font-black ${highlight ? 'text-gold' : 'text-navy-dark'}`}>
        {value.toLocaleString('en-US', { maximumFractionDigits: 2 })} <span className="text-sm font-normal opacity-80">{unit}</span>
      </div>
    </div>
  );
}

function ResultRow({ label, value, unit, color, small }: { label: string; value: number; unit: string; color?: 'success' | 'warn' | 'danger'; small?: boolean }) {
  const colorClass = color === 'success' ? 'text-success-dark' : color === 'warn' ? 'text-warning-dark' : color === 'danger' ? 'text-danger-dark' : 'text-ink';
  return (
    <div className={`flex justify-between items-center ${small ? 'text-xs py-1' : 'text-sm py-1.5'} border-b border-gray-100`}>
      <span className="text-ink-secondary">{label}</span>
      <span className={`font-bold ${colorClass}`}>{value.toLocaleString('en-US', { maximumFractionDigits: 2 })} <span className="text-xs font-normal opacity-70">{unit}</span></span>
    </div>
  );
}

function Insight({ children }: { children: React.ReactNode }) {
  return <div className="bg-info-bg border border-info/30 rounded-xl p-3 text-xs text-info-dark mt-3">{children}</div>;
}
